import { ReactElement } from 'react';
import dynamic from 'next/dynamic';
import { HomepageSlice } from '@adapters/prismic/types/homepage';
import {
  FormEmbedSlice,
  HomepageValuePropSlice,
  PageType,
} from '@adapters/prismic/types/slices';
import BlogArticlesGrid from '@components/cms/BlogArticlesGrid';
import HeroImage from '@components/cms/Slices/HeroImage';
import Container from '@components/global/Container';
import { useTranslation } from 'next-i18next';
import {
  BrandedStoreSlice,
  BrandedStoreSliceType,
} from '@adapters/prismic/types/brandedStorePage';
import SecondaryPromoBoxes from '@components/cms/Slices/SecondaryPromoBoxes';
import BrandValues from '../BrandedStoreSlices/BrandValues';
import PrimaryPromoBoxes from '@components/cms/Slices/PrimaryPromoBoxes';
import CategoryNavCarousel from '../Slices/CategoryNavCarousel';
import HeroBanner from '../Slices/HeroBanner';
import { Card } from '@components/plp/FindifyCollectionsLoadingTakeover';
import loadingStyles from '@components/plp/FindifyCollectionsLoadingTakeover/loadingStyles.module.scss';
import { AlgonomyPlacement } from '@components/global/Recommendations';
import {
  FEATURE_FLAG_ACTIVITY_NAVIGATION_GRID,
  FEATURE_FLAG_RICH_CONTENT_BLOCK,
  FEATURE_FLAG_BANNER_STRIP_SLICE,
  FEATURE_FLAG_BRAND_TILES,
  FEATURE_FLAG_BUNDLE_STRIP,
  FEATURE_FLAG_CATEGORY_NAVIGATION_TILES,
  FEATURE_FLAG_IMAGE_HOTSPOT,
  FEATURE_FLAG_IMAGE_PRODUCT_STRIP,
  FEATURE_FLAG_LEARN_STRIP,
  FEATURE_FLAG_NEW_PRODUCT_STRIP,
  FEATURE_FLAG_PROMO_TILES,
} from '@config/feature-flag';
import BrandTiles from '_components/BrandTiles';
import mapBrandTilesData from '@adapters/prismic/utils/mapBrandTilesData';
import BundleStrip from '_components/BundleStrip';
import { transformBundleStrip } from '@adapters/prismic/transforms/bundleStrip';
import { useLocale } from '@hooks/useLocale';
import ActivityNavigationGrid from '_components/ActivityNavigationGrid';
import BannerStripSlice from '@components/cms/BannerStripSlice';
import mapBannerStripData from '@adapters/prismic/utils/mapBannerStripData';
import { transformActivityNavigationGrid } from '@adapters/prismic/transforms/activityNavigationGrid';
import RichContentBlock from '_components/RichContentBlock';
import { transformRichContentBlock } from '@adapters/prismic/transforms/richContentBlock';
import CategoryNavigationTiles from '_components/CategoryNavigationTiles';
import { transformCategoryNavigationTiles } from '@adapters/prismic/transforms/categoryNavigationTiles';
import MainHero from '_components/MainHero';
import mapMainHeroData from '@adapters/prismic/utils/mapMainHeroData';
import { HOMEPAGE_HERO_TYPE } from '_utils/GA4/trackMainHeroEvents';
import PromoTiles from '_components/PromoTiles';
import mapPromoTilesData from '@adapters/prismic/utils/mapPromoTilesData';
import LearnStrip from '_components/LearnStrip';
import mapLearnStripData from '@adapters/prismic/utils/mapLearnStripData';
import ImageHotspot from '_components/ImageHotspot';
import {
  transformImageHotspot,
  ImageHotspotSlice,
} from '@adapters/prismic/transforms/imageHotspot';
import ImageProductStrip from '_components/ImageProductStrip';
import {
  transformImageProductStrip,
  ImageProductStripSlice,
} from '@adapters/prismic/transforms/imageProductStrip';
import type { HitProps } from '@components/algolia/Hit';
import type { BundlePromotion } from '@adapters/bigcommerce/transforms/bundlePromotion';

const LinkCardGrid = dynamic(
  () => import('@components/cms/Slices/LinkCardGrid')
);
const IconGrid = dynamic(() => import('@components/cms/Slices/IconGrid'));
const ImageBanner = dynamic(() => import('@components/cms/Slices/ImageBanner'));
const CategoryNavigation = dynamic(
  () => import('@components/cms/Slices/CategoryNavigation')
);
const LinkTiles = dynamic(() => import('@components/cms/Slices/LinkTiles'));
const FormEmbed = dynamic(() => import('@components/cms/Slices/FormEmbed'));
const Recommendations = dynamic(
  () => import('@components/global/Recommendations')
);
const ProductStrip = dynamic(() => import('_components/ProductStrip'));

import styles from './HomepageSliceHandler.module.scss';
import HomepageValueProps from '@components/cms/BrandedStoreSlices/HomepageValueProps/HomepageValueProps';

// Component to handle each different prismic slice type we have defined.
const HomepageSliceHandler = ({
  slice,
  pageType,
  sliceIndex,
  sliceTypeIndex,
  secondSliceZone,
  recommendations,
  sliceProducts,
  slicePromotions,
}: {
  slice?:
    | HomepageSlice
    | BrandedStoreSlice
    | FormEmbedSlice
    | HomepageValuePropSlice;
  pageType: PageType;
  sliceIndex: number;
  /** Position among the page's rendered slices of the same type (ED-592). */
  sliceTypeIndex: number;
  secondSliceZone?: boolean;
  recommendations?: AlgonomyPlacement[];
  /** ED-345/ED-221/ED-230: SKU->product data resolved in getStaticProps for image_hotspot, image_product_strip and bundle_strip slices. */
  sliceProducts?: Record<string, HitProps>;
  slicePromotions?: Record<number, BundlePromotion>;
}): ReactElement | null => {
  const locale = useLocale();
  const { t } = useTranslation('common');

  // sliceIndex restarts at 0 in the below-fold zone, so only the first zone's
  // first slice is the page's first slice.
  const isFirstSlice = !secondSliceZone && sliceIndex === 0;

  const recommendationsHome1 = recommendations?.filter(
    (recommendation) => recommendation.name === 'home_page.home1_rr'
  );

  const recommendationsHome3 = recommendations?.filter(
    (recommendation) => recommendation.name === 'home_page.home3_rr'
  );

  // `image_hotspot` and `image_product_strip` aren't members of the
  // HomepageSlice / BrandedStoreSlice unions (same as
  // CategoryPageSliceHandler), so they're handled as early returns with the
  // type assertion scoped to just each branch.
  if ((slice as { slice_type?: string })?.slice_type === 'image_hotspot') {
    if (!FEATURE_FLAG_IMAGE_HOTSPOT) {
      return null;
    }
    const imageHotspotData = transformImageHotspot(
      slice as unknown as ImageHotspotSlice,
      sliceProducts
    );
    return imageHotspotData ? (
      <ImageHotspot
        tabs={imageHotspotData.tabs}
        heading={imageHotspotData.heading}
        priority={isFirstSlice}
        placement="homepage"
      />
    ) : null;
  }

  if (
    (slice as { slice_type?: string })?.slice_type === 'image_product_strip'
  ) {
    if (!FEATURE_FLAG_IMAGE_PRODUCT_STRIP) {
      return null;
    }
    const imageProductStripData = transformImageProductStrip(
      slice as unknown as ImageProductStripSlice,
      sliceProducts
    );
    return imageProductStripData ? (
      <ImageProductStrip {...imageProductStripData} priority={isFirstSlice} />
    ) : null;
  }

  switch (slice?.slice_type) {
    case BrandedStoreSliceType.mainHero:
      return (
        <MainHero
          data={mapMainHeroData(slice.primary)}
          size="large"
          trackingType={HOMEPAGE_HERO_TYPE}
        />
      );
    case 'hero_image':
      return (
        <HeroImage
          image={slice?.primary?.image}
          link={slice?.primary?.link}
          heading={slice?.primary?.heading}
          content={slice?.primary?.content}
          button_text={slice?.primary?.button_text}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'hero_banner':
      return (
        <HeroBanner
          mediaType={slice.primary?.media_type}
          mobileImage={slice.primary?.mobile_image}
          desktopImage={slice.primary?.desktop_image}
          mobileVideo={slice.primary?.mobile_video}
          desktopVideo={slice.primary?.desktop_video}
          videoLabel={slice.primary?.video_alt_text}
          url={slice.primary?.url}
          blackText={slice.primary?.black_text}
          redText={slice.primary?.red_text}
          redTextPlacement={slice.primary?.red_text_placement}
          redTextNewLine={slice.primary?.red_text_new_line}
          bodyCopy={slice.primary?.body_copy}
          ctaText={slice.primary?.cta_text}
          ctaType={slice.primary?.cta_type}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'primary_promo_boxes':
      return (
        <PrimaryPromoBoxes
          title={slice.primary?.title}
          centerTitle={slice.primary?.center_align}
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'secondary_promo_boxes':
      return (
        <SecondaryPromoBoxes
          title={slice.primary.title}
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'recommendations_home1':
      return FEATURE_FLAG_NEW_PRODUCT_STRIP ? (
        <>
          {recommendationsHome1?.map((recommendation: any) => (
            <ProductStrip
              placement={recommendation}
              key={recommendation.name}
            />
          ))}
        </>
      ) : (
        <Container className={styles.singleRecommendation}>
          {recommendationsHome1 ? (
            recommendationsHome1?.map((recommendation: any) => (
              <Recommendations
                isHome
                placement={recommendation}
                key={recommendation.name}
              />
            ))
          ) : (
            <div className={loadingStyles.rightCol}>
              {[...Array(8)].map((e, i) => (
                <Card key={i} />
              ))}
            </div>
          )}
        </Container>
      );
    case 'recommendations_home3':
      return FEATURE_FLAG_NEW_PRODUCT_STRIP ? (
        <>
          {recommendationsHome3?.map((recommendation: any) => (
            <ProductStrip
              placement={recommendation}
              key={recommendation.name}
            />
          ))}
        </>
      ) : (
        <Container className={styles.singleRecommendation}>
          {recommendationsHome3 ? (
            recommendationsHome3?.map((recommendation: any) => (
              <Recommendations
                isHome
                placement={recommendation}
                key={recommendation.name}
              />
            ))
          ) : (
            <div className={loadingStyles.rightCol}>
              {[...Array(8)].map((e, i) => (
                <Card key={i} />
              ))}
            </div>
          )}
        </Container>
      );
    case 'category_nav_carousel':
      return (
        <CategoryNavCarousel
          title={slice.primary.title}
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'link_card_grid':
      return (
        <LinkCardGrid
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'featured_brands':
      return (
        <IconGrid
          title={slice.primary.section_title}
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'image_banner':
      return (
        <ImageBanner
          image={slice.primary.image}
          title={slice.primary.title}
          content={slice.primary.content}
          text_alignment={slice.primary.text_alignment}
          button_text={slice.primary.button_text}
          button_link={slice.primary.button_link}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'category_navigation':
      return (
        <CategoryNavigation
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'link_tiles':
      return (
        <LinkTiles
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'featured_blog_posts':
      return (
        <Container>
          <BlogArticlesGrid
            blogPosts={slice.blog_posts}
            title={slice?.primary?.heading}
            linkTitle={t('recent.articles.button')}
          />
        </Container>
      );
    case 'formembed':
      return <FormEmbed html={slice.primary.embedcode} />;
    case BrandedStoreSliceType.brandValues:
      return <BrandValues primary={slice?.primary} items={slice?.items} />;
    case BrandedStoreSliceType.valueProps:
      return (
        <HomepageValueProps
          primary={slice?.primary}
          items={slice?.items}
          reduceMarginTop
        />
      );
    case BrandedStoreSliceType.brandTiles:
      return FEATURE_FLAG_BRAND_TILES ? (
        <BrandTiles
          {...mapBrandTilesData(slice.primary, slice.items)}
          priority={!secondSliceZone && sliceIndex === 0}
        />
      ) : null;
    case BrandedStoreSliceType.bundleStrip: {
      const bundleStripData = FEATURE_FLAG_BUNDLE_STRIP
        ? transformBundleStrip(slice, sliceProducts, slicePromotions)
        : null;
      return bundleStripData ? (
        <BundleStrip
          {...bundleStripData}
          placement="homepage"
          priority={!secondSliceZone && sliceIndex === 0}
        />
      ) : null;
    }
    case BrandedStoreSliceType.promoTiles:
      return FEATURE_FLAG_PROMO_TILES ? (
        <PromoTiles {...mapPromoTilesData(slice.primary, slice.items)} />
      ) : null;
    case BrandedStoreSliceType.learnStrip:
      return FEATURE_FLAG_LEARN_STRIP ? (
        <LearnStrip {...mapLearnStripData(slice.primary, slice.items)} />
      ) : null;
    case BrandedStoreSliceType.bannerStrip: {
      if (!FEATURE_FLAG_BANNER_STRIP_SLICE) {
        return null;
      }
      const bannerStripData = mapBannerStripData(slice.primary);
      return bannerStripData ? (
        <BannerStripSlice data={bannerStripData} />
      ) : null;
    }
    case BrandedStoreSliceType.activityNavigationGrid: {
      if (!FEATURE_FLAG_ACTIVITY_NAVIGATION_GRID) {
        return null;
      }
      const activityNavigationGridData = transformActivityNavigationGrid(
        slice,
        locale
      );
      return activityNavigationGridData ? (
        <ActivityNavigationGrid
          heading={activityNavigationGridData.heading}
          body={activityNavigationGridData.body}
          items={activityNavigationGridData.items}
          type="homepage"
        />
      ) : null;
    }
    case BrandedStoreSliceType.richContentBlock: {
      if (!FEATURE_FLAG_RICH_CONTENT_BLOCK) {
        return null;
      }
      const richContentBlockData = transformRichContentBlock(slice);
      return richContentBlockData ? (
        <RichContentBlock
          {...richContentBlockData}
          priority={isFirstSlice}
          tracking={{ interactionLocation: 'homepage', index: sliceTypeIndex }}
        />
      ) : null;
    }
    case BrandedStoreSliceType.categoryNavigationTiles: {
      if (!FEATURE_FLAG_CATEGORY_NAVIGATION_TILES) {
        return null;
      }
      const categoryNavigationTilesData = transformCategoryNavigationTiles(
        slice,
        locale
      );
      return categoryNavigationTilesData ? (
        <CategoryNavigationTiles
          heading={categoryNavigationTilesData.heading}
          shopAll={categoryNavigationTilesData.shopAll}
          items={categoryNavigationTilesData.items}
          layout="carousel"
          interactionLocation="homepage"
          priority={isFirstSlice}
        />
      ) : null;
    }
    default:
      return null;
  }
};

export default HomepageSliceHandler;
